<template>
    <div v-if="isDev" ref="wrapperRef" class="debug-overlay" :style="wrapperStyle">
        <button
            v-if="itemCount > 0"
            class="debug-overlay__toggle"
            :class="{ 'debug-overlay__toggle--dragging': isDragging }"
            @mousedown="startDrag"
            @click.stop="handleToggleClick"
        >
            🐛 Debug
            <span class="debug-overlay__badge">{{ itemCount }}</span>
        </button>

        <div
            v-if="isOpen"
            class="debug-overlay__panel"
            :class="{ 'debug-overlay__panel--maximized': isMaximized }"
            @click.stop
        >
            <div class="debug-overlay__sidebar">
                <button
                    v-for="(_, key) in debugState"
                    :key="key"
                    class="debug-overlay__sidebar-item"
                    :class="{ 'debug-overlay__sidebar-item--active': activeKey === key }"
                    @click="activeKey = key"
                >
                    {{ key }}
                </button>
                <div v-if="itemCount === 0" class="debug-overlay__sidebar-empty">
                    Geen variabelen...
                </div>
            </div>

            <div class="debug-overlay__content">
                <div v-if="activeKey && debugState[activeKey]" class="debug-overlay__header">
                    <div class="debug-overlay__header-info">
                        <h3 class="debug-overlay__title">{{ activeKey }}</h3>
                        <div v-if="activeKey !== debugState[activeKey]?.context" class="debug-overlay__context">
                            📍 {{ debugState[activeKey]?.context }}
                        </div>
                    </div>
                    <div class="debug-overlay__actions">
                        <button
                            class="debug-overlay__action-btn"
                            :title="isMaximized ? 'Verkleinen' : 'Vergroten'"
                            @click="isMaximized = !isMaximized"
                        >
                            <svg v-if="!isMaximized" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" /></svg>
                            <svg v-else xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3" /></svg>
                        </button>
                    </div>
                </div>
                <pre v-if="activeKey && debugState[activeKey]" class="debug-overlay__output">{{ JSON.stringify(debugState[activeKey]?.value, null, 2) }}</pre>
                <div v-else class="debug-overlay__empty">Selecteer een variabele om te inspecteren</div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted, onUnmounted } from 'vue';
import { debugState } from '../utils/debug';

// Only render the overlay in development mode.
const isDev = process.dev;

// Controls whether the debug panel is open or collapsed.
const isOpen = ref(false);

// The key of the currently selected variable in the sidebar.
const activeKey = ref<string | null>(null);

// Whether the panel is expanded to fill most of the screen.
const isMaximized = ref(false);

// Reference to the outer wrapper element, used for drag positioning and click-outside detection.
const wrapperRef = ref<HTMLElement | null>(null);

// --- Drag & drop positioning ---

// Current position of the overlay button on screen.
const buttonPosition = ref({ x: 0, y: 0 });

// Whether the user is currently dragging the button.
const isDragging = ref(false);

// Mouse position at the moment the drag started.
let dragStartMousePosition = { x: 0, y: 0 };

// Button position at the moment the drag started.
let dragStartButtonPosition = { x: 0, y: 0 };

// Tracks whether the user actually moved the button, so we can distinguish
// a drag from a regular click.
let hasMovedDuringDrag = false;

/**
 * Computes the CSS positioning for the wrapper element.
 *
 * When no custom position is set (x and y are both 0), we default to the
 * bottom-right corner via CSS. Once the user drags the button, we switch to
 * absolute left/top positioning.
 *
 * @returns {Object} The CSS styles for positioning.
 */
const wrapperStyle = computed(() => {
    const hasCustomPosition = buttonPosition.value.x !== 0 || buttonPosition.value.y !== 0;

    if (!hasCustomPosition) {
        return { bottom: '20px', right: '20px' };
    }

    return {
        left: `${buttonPosition.value.x}px`,
        top: `${buttonPosition.value.y}px`,
        bottom: 'auto',
        right: 'auto',
    };
});

// Total number of variables currently registered in the debug state.
const itemCount = computed(() => Object.keys(debugState).length);

/**
 * Begins tracking a drag operation when the user presses the mouse button
 * down on the toggle button. Stores the starting positions and attaches
 * global mouse event listeners.
 *
 * @param {MouseEvent} event - The mouse down event.
 */
const startDrag = (event: MouseEvent) => {
    isDragging.value = true;
    hasMovedDuringDrag = false;

    // If the button is still in the default corner position, calculate its
    // actual pixel coordinates so we can use them as the drag start point.
    const isAtDefaultPosition = buttonPosition.value.x === 0 && buttonPosition.value.y === 0;
    if (isAtDefaultPosition && wrapperRef.value) {
        const boundingRect = wrapperRef.value.getBoundingClientRect();
        buttonPosition.value.x = boundingRect.left;
        buttonPosition.value.y = boundingRect.top;
    }

    dragStartMousePosition = { x: event.clientX, y: event.clientY };
    dragStartButtonPosition = { x: buttonPosition.value.x, y: buttonPosition.value.y };

    window.addEventListener('mousemove', onDrag);
    window.addEventListener('mouseup', stopDrag);
};

/**
 * Updates the button position as the user moves the mouse while dragging.
 * Sets `hasMovedDuringDrag` once the movement exceeds a 5px threshold,
 * preventing unintentional clicks when the user barely moves the mouse.
 *
 * @param {MouseEvent} event - The mouse move event.
 */
const onDrag = (event: MouseEvent) => {
    if (!isDragging.value || !wrapperRef.value) {
        return;
    }

    const deltaX = event.clientX - dragStartMousePosition.x;
    const deltaY = event.clientY - dragStartMousePosition.y;

    const movedBeyondThreshold = Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5;
    if (movedBeyondThreshold) {
        hasMovedDuringDrag = true;
    }

    // Proposed new position.
    const newX = dragStartButtonPosition.x + deltaX;
    const newY = dragStartButtonPosition.y + deltaY;

    // Boundary check: ensure the button stays within the visible viewport.
    const { offsetWidth, offsetHeight } = wrapperRef.value;
    const maxX = window.innerWidth - offsetWidth - 10; // Keep 10px from edges.
    const maxY = window.innerHeight - offsetHeight - 10;

    buttonPosition.value.x = Math.max(10, Math.min(newX, maxX));
    buttonPosition.value.y = Math.max(10, Math.min(newY, maxY));
};

/**
 * Ends the drag operation, removes the global event listeners, and
 * persists the new position to localStorage so it survives page reloads.
 */
const stopDrag = () => {
    isDragging.value = false;

    window.removeEventListener('mousemove', onDrag);
    window.removeEventListener('mouseup', stopDrag);

    sessionStorage.setItem('debug-pos', JSON.stringify(buttonPosition.value));
};

/**
 * Handles a click on the toggle button. Only opens or closes the panel if
 * the user did not drag the button — otherwise the click is ignored.
 */
const handleToggleClick = () => {
    if (!hasMovedDuringDrag) {
        isOpen.value = !isOpen.value;
    }
};

/**
 * Closes the debug panel when the user clicks anywhere outside of it.
 *
 * @param {MouseEvent} event - The click event.
 */
const handleClickOutside = (event: MouseEvent) => {
    const clickedOutsidePanel = wrapperRef.value && !wrapperRef.value.contains(event.target as Node);

    if (isOpen.value && clickedOutsidePanel) {
        isOpen.value = false;
        isMaximized.value = false;
    }
};

/**
 * Handles a resize event by ensuring the button stays within the new viewport.
 */
const handleResize = () => {
    if (wrapperRef.value && (buttonPosition.value.x !== 0 || buttonPosition.value.y !== 0)) {
        const { offsetWidth, offsetHeight } = wrapperRef.value;
        const maxX = window.innerWidth - offsetWidth - 10;
        const maxY = window.innerHeight - offsetHeight - 10;

        buttonPosition.value.x = Math.max(10, Math.min(buttonPosition.value.x, maxX));
        buttonPosition.value.y = Math.max(10, Math.min(buttonPosition.value.y, maxY));
    }
};

onMounted(() => {
    window.addEventListener('click', handleClickOutside);
    window.addEventListener('resize', handleResize);

    // Restore the previously saved button position from sessionStorage.
    const savedPosition = sessionStorage.getItem('debug-pos');
    if (savedPosition) {
        try {
            const position = JSON.parse(savedPosition);

            // Ensure the restored position is still within the current viewport bounds
            // so the button doesn't start off-screen after a resize.
            if (wrapperRef.value) {
                const { offsetWidth, offsetHeight } = wrapperRef.value;
                const maxX = window.innerWidth - offsetWidth - 10;
                const maxY = window.innerHeight - offsetHeight - 10;

                position.x = Math.max(10, Math.min(position.x, maxX));
                position.y = Math.max(10, Math.min(position.y, maxY));
            }

            buttonPosition.value = position;
        } catch {
            // Ignore invalid JSON — the button will stay at the default position.
        }
    }
});

onUnmounted(() => {
    window.removeEventListener('click', handleClickOutside);
    window.removeEventListener('resize', handleResize);
    window.removeEventListener('mousemove', onDrag);
    window.removeEventListener('mouseup', stopDrag);
});

// Keep the sidebar selection in sync when variables are added or removed.
watch(() => debugState, (updatedState) => {
    const availableKeys = Object.keys(updatedState);

    // No selection yet — automatically select the first incoming variable.
    if (!activeKey.value && availableKeys.length > 0) {
        activeKey.value = availableKeys[0] ?? null;
        return;
    }

    // The selected variable was removed (e.g. after a clear) — fall back to the first one.
    const selectedKeyStillExists = activeKey.value && availableKeys.includes(activeKey.value);
    if (!selectedKeyStillExists && availableKeys.length > 0) {
        activeKey.value = availableKeys[0] ?? null;
        return;
    }

    // Everything was cleared — reset the selection.
    if (availableKeys.length === 0) {
        activeKey.value = null;
    }
}, { deep: true, immediate: true });
</script>

<style scoped lang="less">
.debug-overlay {
    position: fixed;
    z-index: 999999;

    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
}

.debug-overlay__toggle {
    position: relative;

    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    border: 1px solid #00dc82;

    font-size: 14px;
    font-weight: 500;

    background: #00dc82;
    color: #000;
    border-radius: 20px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transition: transform 0.2s, background 0.2s, box-shadow 0.2s;

    cursor: grab;
    user-select: none;


    &:hover {
        transform: translateY(-2px);
    }
}

.debug-overlay__toggle--dragging {
    transform: scale(1.05);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);

    cursor: grabbing;
}

.debug-overlay__badge {
    padding: 2px 6px;

    font-size: 11px;
    font-weight: bold;

    background: #000;
    color: #00dc82;
    border-radius: 10px;
}

.debug-overlay__panel {
    position: absolute;
    z-index: 1000000;
    bottom: 50px;
    right: 0;

    display: flex;
    width: 600px;
    max-width: 90vw;
    height: 400px;
    max-height: 80vh;
    border: 1px solid #333;

    background: #1e1e1e;
    color: #d4d4d4;
    border-radius: 8px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);

    overflow: hidden;
}

.debug-overlay__panel--maximized {
    position: fixed;
    top: 5vh;
    left: 5vw;
    bottom: auto;
    right: auto;

    width: 90vw;
    height: 90vh;
    max-width: none;
    max-height: none;
}

.debug-overlay__sidebar {
    display: flex;
    flex-direction: column;
    width: 200px;
    border-right: 1px solid #333;

    background: #252526;

    overflow-y: auto;
}

.debug-overlay__sidebar-item {
    padding: 12px;
    border: none;
    border-bottom: 1px solid #333;

    font-size: 13px;
    text-align: left;
    white-space: nowrap;

    background: none;
    color: #ccc;
    transition: background 0.2s;

    cursor: pointer;
    overflow: hidden;
    text-overflow: ellipsis;


    &:hover {
        background: #2a2d2e;
    }
}

.debug-overlay__sidebar-item--active {
    border-left: 3px solid #00dc82;

    color: #fff;

    background: #37373d;
}

.debug-overlay__sidebar-empty {
    padding: 20px;

    font-size: 12px;
    text-align: center;

    color: #666;
}

.debug-overlay__content {
    display: flex;
    flex-direction: column;
    flex: 1;

    overflow: hidden;
}

.debug-overlay__header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 10px 15px;
    border-bottom: 1px solid #333;

    background: #252526;
}

.debug-overlay__header-info {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.debug-overlay__title {
    margin: 0;

    font-size: 14px;
    word-break: break-all;

    color: #00dc82;
}

.debug-overlay__context {
    display: inline-block;
    padding: 2px 6px;
    border: 1px solid #333;

    font-size: 11px;

    background: #111;
    color: #888;
    border-radius: 4px;
}

.debug-overlay__actions {
    display: flex;
    align-items: center;
    gap: 8px;
}

.debug-overlay__action-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 4px;
    border: 1px solid #444;

    font-size: 11px;

    background: #333;
    color: #ccc;
    border-radius: 4px;
    transition: background 0.2s;

    cursor: pointer;


    &:hover {
        background: #444;
        color: white;
    }
}

.debug-overlay__output {
    flex: 1;
    margin: 0;
    padding: 15px;

    font-size: 13px;
    line-height: 1.5;

    background: #1e1e1e;

    overflow: auto;
}

.debug-overlay__empty {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;

    font-size: 13px;

    color: #666;
}
</style>
